import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import {
  CaretLeft, Envelope, FloppyDisk, PaperPlaneTilt, ArrowClockwise,
} from "@phosphor-icons/react";
import apiClient from "../../lib/api";
import { toast } from "sonner";

const TEMPLATE_TABS = [
  { key: "welcome",          label: "Bienvenida",       color: "text-emerald-400" },
  { key: "new_pick",         label: "Nueva jugada",     color: "text-blue-400" },
  { key: "expiry_reminder",  label: "Recordatorio",     color: "text-yellow-400" },
  { key: "expired",          label: "Expirada",         color: "text-orange-400" },
  { key: "suspension",       label: "Suspensión",       color: "text-red-400" },
];

const VARIABLE_HINTS = {
  welcome:         ["{{nombre}}", "{{trial_dias}}"],
  new_pick:        ["{{nombre}}", "{{emoji_deporte}}", "{{partido}}", "{{deporte}}", "{{pick}}", "{{cuota}}", "{{confianza}}", "{{pick_id}}"],
  expiry_reminder: ["{{nombre}}", "{{dias_restantes}}", "{{fecha_vencimiento}}"],
  expired:         ["{{nombre}}"],
  suspension:      ["{{nombre}}", "{{dias_vencido}}"],
};

export default function AdminEmailTemplatesPage() {
  const [templates, setTemplates] = useState({});
  const [activeTab, setActiveTab] = useState("welcome");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);
  const [testEmail, setTestEmail] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await apiClient.get("/admin/email-templates");
      setTemplates(r.data.templates || {});
    } catch {
      toast.error("Error al cargar plantillas");
    }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const tpl = templates[activeTab] || {};

  const update = (field, value) => {
    setTemplates(prev => ({
      ...prev,
      [activeTab]: { ...prev[activeTab], [field]: value },
    }));
  };

  const save = async () => {
    setSaving(true);
    try {
      await apiClient.put(`/admin/email-templates/${activeTab}`, tpl);
      toast.success("Plantilla guardada");
    } catch {
      toast.error("Error al guardar");
    }
    setSaving(false);
  };

  const sendTest = async () => {
    if (!testEmail.trim()) {
      toast.error("Ingresa un email de destino");
      return;
    }
    setSending(true);
    try {
      const r = await apiClient.post("/admin/send-test-email", {
        type: activeTab,
        email: testEmail.trim(),
      });
      toast.success(`Email de prueba enviado a ${r.data.to}`);
    } catch {
      toast.error("Error al enviar email de prueba");
    }
    setSending(false);
  };

  return (
    <div className="min-h-screen bg-background pb-16">
      {/* Header */}
      <div className="border-b border-white/[0.06] bg-surface/50 backdrop-blur-sm sticky top-0 z-20">
        <div className="max-w-4xl mx-auto px-4 lg:px-6 py-4 flex items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <Link to="/admin" className="text-zinc-500 hover:text-foreground transition-colors">
              <CaretLeft weight="bold" className="w-5 h-5" />
            </Link>
            <div className="flex items-center gap-2">
              <Envelope weight="duotone" className="w-5 h-5 text-yellow-400" />
              <span className="font-bold text-lg">Plantillas de Email</span>
            </div>
          </div>
          <button
            onClick={load}
            className="p-2 rounded-lg text-zinc-500 hover:text-white hover:bg-white/[0.05] transition-all"
          >
            <ArrowClockwise className="w-4 h-4" />
          </button>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 lg:px-6 py-8">
        {/* Tabs */}
        <div className="flex flex-wrap gap-2 mb-6">
          {TEMPLATE_TABS.map(t => (
            <button
              key={t.key}
              data-testid={`tpl-tab-${t.key}`}
              onClick={() => setActiveTab(t.key)}
              className={`px-4 py-2 rounded-lg text-sm font-semibold border transition-all ${
                activeTab === t.key
                  ? `${t.color} bg-white/[0.06] border-white/[0.12]`
                  : "text-zinc-500 border-white/[0.06] hover:text-white hover:border-white/[0.12]"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="surface rounded-2xl p-12 text-center text-zinc-500">Cargando plantillas...</div>
        ) : (
          <div className="grid lg:grid-cols-3 gap-6">
            {/* Editor */}
            <div className="lg:col-span-2 space-y-4">
              <div className="surface rounded-2xl p-6 space-y-4">
                <div className="flex items-center justify-between mb-2">
                  <div>
                    <div className="text-[10px] text-zinc-500 uppercase tracking-[0.2em] font-bold mb-1">Plantilla activa</div>
                    <div className="font-bold text-lg">{TEMPLATE_TABS.find(t => t.key === activeTab)?.label}</div>
                  </div>
                </div>

                {/* Subject */}
                <div>
                  <label className="text-xs text-zinc-500 uppercase tracking-wider font-bold mb-1.5 block">
                    Asunto del email
                  </label>
                  <input
                    data-testid="tpl-subject"
                    className="input-field w-full font-mono text-sm"
                    value={tpl.subject || ""}
                    onChange={e => update("subject", e.target.value)}
                  />
                </div>

                {/* Preview text */}
                <div>
                  <label className="text-xs text-zinc-500 uppercase tracking-wider font-bold mb-1.5 block">
                    Texto de previsualización
                  </label>
                  <input
                    data-testid="tpl-preview-text"
                    className="input-field w-full text-sm"
                    value={tpl.preview_text || ""}
                    onChange={e => update("preview_text", e.target.value)}
                  />
                </div>

                {/* Opening */}
                <div>
                  <label className="text-xs text-zinc-500 uppercase tracking-wider font-bold mb-1.5 block">
                    Saludo / apertura
                  </label>
                  <input
                    data-testid="tpl-opening"
                    className="input-field w-full text-sm"
                    value={tpl.opening || ""}
                    onChange={e => update("opening", e.target.value)}
                  />
                </div>

                {/* Body */}
                <div>
                  <label className="text-xs text-zinc-500 uppercase tracking-wider font-bold mb-1.5 block">
                    Cuerpo del mensaje
                  </label>
                  <textarea
                    data-testid="tpl-body"
                    rows={5}
                    className="input-field w-full text-sm font-mono resize-none"
                    value={tpl.body || ""}
                    onChange={e => update("body", e.target.value)}
                  />
                </div>

                {/* CTA */}
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs text-zinc-500 uppercase tracking-wider font-bold mb-1.5 block">
                      Texto del botón
                    </label>
                    <input
                      data-testid="tpl-cta-text"
                      className="input-field w-full text-sm"
                      value={tpl.cta_text || ""}
                      onChange={e => update("cta_text", e.target.value)}
                    />
                  </div>
                  <div>
                    <label className="text-xs text-zinc-500 uppercase tracking-wider font-bold mb-1.5 block">
                      URL del botón
                    </label>
                    <input
                      data-testid="tpl-cta-url"
                      className="input-field w-full text-sm font-mono"
                      value={tpl.cta_url || ""}
                      onChange={e => update("cta_url", e.target.value)}
                    />
                  </div>
                </div>

                {/* Actions */}
                <div className="flex items-center justify-end gap-3 pt-2 border-t border-white/[0.06] mt-2">
                  <button
                    onClick={save}
                    disabled={saving}
                    data-testid="tpl-save-btn"
                    className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-yellow-400 hover:bg-yellow-300 text-black text-sm font-bold transition-all disabled:opacity-50"
                  >
                    <FloppyDisk weight="bold" className="w-4 h-4" />
                    {saving ? "Guardando..." : "Guardar plantilla"}
                  </button>
                </div>
              </div>

              {/* Send test email */}
              <div className="surface rounded-2xl p-5">
                <h3 className="font-bold text-sm mb-4">Enviar email de prueba</h3>
                <div className="flex gap-2">
                  <input
                    data-testid="tpl-test-email"
                    className="input-field flex-1 text-sm"
                    type="email"
                    placeholder="correo@ejemplo.com"
                    value={testEmail}
                    onChange={e => setTestEmail(e.target.value)}
                    onKeyDown={e => e.key === "Enter" && sendTest()}
                  />
                  <button
                    onClick={sendTest}
                    disabled={sending}
                    data-testid="tpl-send-test-btn"
                    className="flex items-center gap-2 px-4 py-2 rounded-xl bg-blue-500/10 border border-blue-500/20 text-blue-400 hover:bg-blue-500/20 transition-all text-sm font-semibold disabled:opacity-50 whitespace-nowrap"
                  >
                    <PaperPlaneTilt className="w-4 h-4" />
                    {sending ? "Enviando..." : "Enviar prueba"}
                  </button>
                </div>
                <p className="text-xs text-zinc-600 mt-2">
                  Nota: Resend sandbox solo envía a emails verificados. En producción con dominio propio funciona para todos.
                </p>
              </div>
            </div>

            {/* Sidebar: variables */}
            <div className="space-y-4">
              <div className="surface rounded-2xl p-5">
                <h3 className="font-bold text-sm mb-3">Variables disponibles</h3>
                <p className="text-xs text-zinc-500 mb-3">
                  Usa estas variables en asunto, cuerpo o apertura. Se reemplazan automáticamente al enviar.
                </p>
                <div className="space-y-1.5">
                  {(VARIABLE_HINTS[activeTab] || []).map(v => (
                    <div
                      key={v}
                      className="px-3 py-1.5 bg-white/[0.03] border border-white/[0.06] rounded-lg font-mono text-xs text-yellow-400"
                    >
                      {v}
                    </div>
                  ))}
                </div>
              </div>

              {/* Preview skeleton */}
              <div className="surface rounded-2xl p-5">
                <h3 className="font-bold text-sm mb-3">Vista previa del asunto</h3>
                <div className="bg-white/[0.03] border border-white/[0.06] rounded-xl p-4">
                  <div className="text-[10px] text-zinc-600 uppercase tracking-wider mb-1">De:</div>
                  <div className="text-xs text-zinc-400">PicksVault &lt;noreply@picksvault.pro&gt;</div>
                  <div className="text-[10px] text-zinc-600 uppercase tracking-wider mt-3 mb-1">Asunto:</div>
                  <div className="text-sm font-semibold text-foreground break-all">
                    {(tpl.subject || "").replace("{{nombre}}", "Carlos").replace("{{emoji_deporte}}", "⚽").replace("{{partido}}", "Real Madrid vs Barça").replace("{{dias_restantes}}", "3")}
                  </div>
                  {tpl.preview_text && (
                    <>
                      <div className="text-[10px] text-zinc-600 uppercase tracking-wider mt-3 mb-1">Preview:</div>
                      <div className="text-xs text-zinc-500 italic">{tpl.preview_text}</div>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
